import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { LOCAL__ui_set_string } from '@/state/ui/_base/LOCAL__ui_set_string';
import { LOCAL__ui_generation_graph_set_field_to_create_new_edge } from '@/state/ui/generation/LOCAL__ui_generation_graph_set_field_to_create_new_edge';
import { UTILS__generation } from '@/utils/UTILS__generation';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_type_enum, assert_type_string } from '@shared/asserts/utils_asserts';
import { assert_string_uuid } from '@shared/asserts/utils_asserts_string';
import { Connection, OnConnectStartParams, Position } from '@xyflow/react';
import { useCallback, useState } from 'react';

export function HOOK_GRAPH_FLOW__connections() {
  /*
   * STORE
   */
  const dispatch = use_dispatch();
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_graph__new_edge_field_position } = use_slice_ui(['g_graph__new_edge_field_position']);

  /*
   * STATE
   */

  const { graph } = generation_current.tool;

  const [is_connecting, set_is_connecting] = useState(false);

  /*
   * EVENTS
   */
  const onConnectStart = useCallback(
    (event: MouseEvent | TouchEvent, params: OnConnectStartParams) => {
      assert_type_string(params.nodeId);
      assert_string_uuid(params.nodeId);

      assert_type_string(params.handleId);

      let [field_id, position] = params.handleId.split('_');

      assert_string_uuid(field_id);
      assert_type_enum(position, Position);

      dispatch(LOCAL__ui_set_string({ value: position, key: 'g_graph__new_edge_field_position', valid_values: Object.values(Position) }));

      let node = graph.node__get(params.nodeId);

      let field = node.field__get(field_id);

      dispatch(LOCAL__ui_generation_graph_set_field_to_create_new_edge(field));

      set_is_connecting(true);
    },
    [graph],
  );

  function onConnectEnd(event: MouseEvent | TouchEvent | null) {
    if (!is_connecting) return;

    set_is_connecting(false);

    clear_field_to_create_new_edge();
  }

  async function onConnect(connection: Connection) {
    let { sourceHandle, targetHandle, source, target } = connection;

    assert_string_uuid(source);
    assert_string_uuid(target);

    assert_type_string(sourceHandle);
    assert_type_string(targetHandle);

    const [field_id_source, position_source] = sourceHandle.split('_');
    const [field_id_target, position_target] = targetHandle.split('_');

    assert_string_uuid(field_id_source);
    assert_string_uuid(field_id_target);

    let [field_id_from, field_id_to] = [field_id_source, field_id_target];

    if (g_graph__new_edge_field_position == Position.Left) [field_id_to, field_id_from] = [field_id_from, field_id_to];

    let node_from = graph.node__get(source);
    let node_to = graph.node__get(target);

    let field_from = node_from.field__get(field_id_source);
    let field_to = node_to.field__get(field_id_target);

    let graph_new = graph.clone();

    //delete active edges
    let edges_to = graph_new.edges__get_to([field_to]);
    graph_new.edges__delete(edges_to);

    graph_new.edge__add_for_fields(field_from, field_to);

    await UTILS__graph_data.update(graph_new);

    await UTILS__generation.update_tool_name_as_custom(generation_current.tool);

    clear_field_to_create_new_edge();

    set_is_connecting(false);
  }

  function clear_field_to_create_new_edge() {
    dispatch(LOCAL__ui_set_string({ value: '', key: 'g_graph__new_edge_field_position', valid_values: [] }));

    dispatch(LOCAL__ui_generation_graph_set_field_to_create_new_edge());
  }

  /*
   * RESULT
   */

  return { onConnectStart, onConnectEnd, onConnect };
}
